import { useEffect, useMemo, useState } from 'react';
import {
  ContentsSchema,
  RoomJSONStructureItem,
  RoomStructureContent,
  RoomStructureFolder,
  RoomStructureHeader,
} from '../../../definitions/schemas';
import {
  Breadcrumb,
  FolderChangeEntry,
  FolderSchema,
} from '../../../types/Folder';
import {
  dedupeContentsUnion,
  deriveFolderCoverSigned,
  matchStructureItems,
} from '../../../lib/helper/RoomStructureHelper';
import { fetchFolderShared } from '../../../lib/helper/FolderFetchCache';
import { restoreTextBoxIds } from '../../RoomTemplates/Save/SaveTemplatePageHelper';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';

export interface FolderDataBindings {
  folderChanges: Map<string, FolderChangeEntry>;
  initFolderChange: (
    folderId: string,
    entry: Omit<FolderChangeEntry, 'dirty'>
  ) => void;
  getFolderChange: (folderId: string) => FolderChangeEntry | undefined;
  selectedContent: ContentsSchema;
  setSelectedContent: React.Dispatch<React.SetStateAction<ContentsSchema>>;
}

export interface FolderData {
  folder: FolderSchema | undefined;
  loading: boolean;
  error: boolean;
  structureItems: RoomJSONStructureItem[];
  structureContents: RoomStructureContent[];
  structureHeaders: RoomStructureHeader[];
  structureFolders: RoomStructureFolder[];
  breadcrumbs: Breadcrumb[];
  roomObjectAclsContents?: string[];
}

const EMPTY: FolderData = {
  folder: undefined,
  loading: false,
  error: false,
  structureItems: [],
  structureContents: [],
  structureHeaders: [],
  structureFolders: [],
  breadcrumbs: [],
};

/**
 * Fetches a folder by id and derives its matched structure.
 *
 * When `bindings` is provided (edit path), the fetch is skipped if the folder
 * is already in `folderChanges`, and the result is seeded back into it.
 * When `bindings` is omitted (read-only path), the folder is always fetched
 * fresh from the server.
 */
const useFolderData = (
  folderId: string | null | undefined,
  options?: { bindings?: FolderDataBindings; contactId?: string }
): FolderData => {
  const { bindings, contactId } = options ?? {};
  const [fetchedFolder, setFetchedFolder] = useState<FolderSchema>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const isInlineIframeViewerEnabled = useFeatureFlag(
    OrganizationFlagEnum.INLINE_IFRAME_VIEWER
  );

  useEffect(() => {
    if (!folderId) {
      setFetchedFolder(undefined);
      setLoading(false);
      setError(false);
      return;
    }

    // Edit path cache hit — skip fetch if the folder was already fetched
    if (bindings?.folderChanges.get(folderId)?.fetched) {
      setFetchedFolder(undefined);
      setLoading(false);
      setError(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(false);

    fetchFolderShared(folderId, contactId)
      .then((data) => {
        if (!active) return;

        // Restore uuids on blank-id text boxes (saved with '' so the backend
        // forks a new text content) so they can be matched/rendered again —
        // mirrors postProcessTemplate for the template root. Done once at fetch
        // time so BOTH the edit path (folderChanges seeding below) and the
        // read-only/preview path (fetchedFolder-derived structure) get the same
        // non-empty ids and don't drop the item from the render pool.
        const normalizedItems = restoreTextBoxIds(data.structure?.items);
        setFetchedFolder({
          ...data,
          structure: { ...data.structure, items: normalizedItems },
        });

        if (bindings) {
          // Merge fetched data into any existing entry (e.g. pre-fetch rename),
          // preserving the local name and marking as fetched so we skip re-fetching.
          bindings.initFolderChange(folderId, {
            name: data.name,
            structure: normalizedItems,
            breadcrumbs: data.breadcrumbs ?? [],
            fetched: true,
          });
          if (data.contents && data.contents.length > 0) {
            // Dedupe against whatever's already pooled: applyFolderTargetedContents
            // can independently fetch and pool this same folder's contents (when
            // content is targeted at it before it's been fetched here), and
            // without this both fetches' resolution would each append the same
            // records, duplicating tiles.
            bindings.setSelectedContent((prev) =>
              dedupeContentsUnion(prev, data.contents!)
            );
          }
        }
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        if (bindings?.folderChanges.get(folderId)) {
          setLoading(false);
          return;
        }
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [folderId, bindings, contactId]);

  const folderEntry = bindings?.getFolderChange(folderId ?? '');

  // Apply any in-flight renames from folderChanges to each breadcrumb entry so
  // that renaming a parent folder is immediately reflected in its children's
  // breadcrumb trails without requiring a re-fetch.
  const breadcrumbs = useMemo(() => {
    const rawCrumbs =
      folderEntry?.breadcrumbs ?? fetchedFolder?.breadcrumbs ?? [];
    if (!bindings) return rawCrumbs;
    return rawCrumbs.map((crumb) => {
      const change = bindings.getFolderChange(crumb.id);
      return change?.name ? { ...crumb, name: change.name } : crumb;
    });
  }, [folderEntry?.breadcrumbs, fetchedFolder?.breadcrumbs, bindings]);

  // Edit path: structure comes from folderChanges (mutation-aware).
  // Read-only path: structure comes from the fetched folder.
  const structure = useMemo(
    () => folderEntry?.structure ?? fetchedFolder?.structure?.items ?? [],
    [folderEntry, fetchedFolder]
  );

  const folder: FolderSchema | undefined = useMemo(() => {
    if (folderEntry && folderId) {
      return {
        ...(fetchedFolder ?? {
          id: folderId,
          itemCount: 0,
          depth: 0,
          structure: { items: [] },
        }),
        name: folderEntry.name,
        itemCount: folderEntry.fetched
          ? folderEntry.structure.length
          : fetchedFolder?.itemCount ?? 0,
      };
    }
    return fetchedFolder;
  }, [folderEntry, fetchedFolder, folderId]);

  const selectedContent = useMemo(
    () => bindings?.selectedContent ?? fetchedFolder?.contents ?? [],
    [bindings?.selectedContent, fetchedFolder?.contents]
  );

  const contents: ContentsSchema = useMemo(() => {
    const structureIds = new Set(structure.map((item) => item.id));
    return selectedContent.filter((c) => c.id && structureIds.has(c.id));
  }, [structure, selectedContent]);

  const [structureHeaders, structureContents, rawStructureFolders] = useMemo(
    () =>
      matchStructureItems(
        contents,
        { items: structure },
        isInlineIframeViewerEnabled
      ),
    [contents, structure, isInlineIframeViewerEnabled]
  );

  // Override itemCount for child folders from their folderChanges entry so the
  // count reflects local additions/removals before the room is saved. Once a
  // child folder has been fetched, also derive its cover locally (mirroring the
  // backend) so it stays in sync with its own first content; otherwise keep the
  // backend-provided coverSigned.
  const structureFolders = useMemo(
    () =>
      bindings
        ? rawStructureFolders.map((f) => {
            const childEntry = f.id
              ? bindings.folderChanges.get(f.id)
              : undefined;
            if (!childEntry) {
              return f;
            }
            return {
              ...f,
              name: childEntry.name,
              itemCount: childEntry.fetched
                ? childEntry.structure.length
                : f.itemCount,
              ...(childEntry.fetched
                ? {
                    coverSigned: deriveFolderCoverSigned(
                      childEntry.structure,
                      selectedContent
                    ),
                  }
                : {}),
            };
          })
        : rawStructureFolders,
    [rawStructureFolders, bindings, selectedContent]
  );

  if (!folderId) return EMPTY;

  return {
    folder,
    loading,
    error,
    structureItems: structure,
    structureContents,
    structureHeaders,
    structureFolders,
    breadcrumbs,
    roomObjectAclsContents: fetchedFolder?.roomObjectAclsContents,
  };
};

export default useFolderData;
